import { ConsentFormDefinition } from '../types';

export const minorPatientTelehealth: ConsentFormDefinition = {
  formKey: 'minor-patient-telehealth',
  title: 'Minor Patient Telehealth Consent',
  pdfTitle: 'Parental/Guardian Consent for Minor Patient Telehealth Services',
  intro:
    'This form authorizes telehealth therapy and behavioral health services for your minor child through {providerName} via the HIPAA LINK secure platform. A parent or legal guardian must complete and sign this form.',
  sections: [
    {
      id: 'minor-info',
      navLabel: 'Minor Info',
      heading: "Your Child's Information",
      blocks: [
        {
          type: 'field-group',
          layout: '2-2',
          pdfTable: true,
          fields: [
            {
              type: 'text',
              id: 'minorFirstName',
              label: "Child's First Name",
              placeholder: 'First name',
              required: true,
              pdfMinWidth: 160,
            },
            {
              type: 'text',
              id: 'minorLastName',
              label: "Child's Last Name",
              placeholder: 'Last name',
              required: true,
              pdfMinWidth: 160,
            },
            {
              type: 'date',
              id: 'minorDob',
              label: "Child's Date of Birth",
              required: true,
            },
            {
              type: 'computed',
              id: 'minorAge',
              label: 'Current Age',
              compute: 'ageFromDob',
              sourceField: 'minorDob',
              pdfHide: true,
            },
          ],
        },
      ],
    },
    {
      id: 'guardian-info',
      navLabel: 'Guardian Info',
      heading: 'Parent or Legal Guardian Information',
      blocks: [
        {
          type: 'field-group',
          layout: '2-2',
          pdfTable: true,
          fields: [
            {
              type: 'text',
              id: 'guardianFirstName',
              label: 'Your First Name',
              placeholder: 'First name',
              required: true,
              pdfMinWidth: 140,
            },
            {
              type: 'text',
              id: 'guardianLastName',
              label: 'Your Last Name',
              placeholder: 'Last name',
              required: true,
              pdfMinWidth: 140,
            },
            {
              type: 'select',
              id: 'relationship',
              label: 'Your Relationship to Child',
              options: [
                { value: 'mother', label: 'Mother' },
                { value: 'father', label: 'Father' },
                { value: 'legal-guardian', label: 'Legal Guardian' },
                { value: 'custodial-parent', label: 'Custodial Parent' },
                { value: 'other', label: 'Other' },
              ],
            },
            {
              type: 'text',
              id: 'guardianPhone',
              label: 'Your Phone Number',
              placeholder: '(123) 456-7890',
              required: true,
              pdfMinWidth: 140,
            },
          ],
        },
      ],
    },
    {
      id: 'what-is-telehealth',
      navLabel: 'What Is Telehealth?',
      heading: 'Telehealth Therapy for Your Child',
      blocks: [
        {
          type: 'paragraph',
          text: 'Telehealth allows your child to receive therapy, counseling, or psychiatric care from home using secure video, phone, or messaging with a licensed therapist or psychiatrist. Your child will connect from a private location.',
        },
        {
          type: 'paragraph',
          text: 'How sessions work:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Video sessions: Your child and therapist see and hear each other',
            'Phone sessions: Audio only communication',
            'Secure messaging: Non-urgent questions between sessions',
            'Session length: Typically 45-50 minutes (therapist will discuss with you)',
          ],
        },
      ],
    },
    {
      id: 'service-types',
      navLabel: 'Services & Parental Presence',
      heading: 'Types of Services and Your Involvement',
      blocks: [
        {
          type: 'paragraph',
          text: 'Please check the types of services your child will receive:',
          bold: true,
        },
        {
          type: 'checklist',
          id: 'service-types',
          options: [
            { id: 'individual-therapy', label: 'Individual Therapy (one-on-one with therapist)' },
            { id: 'family-therapy', label: 'Family Therapy (sessions with parents/guardians and child)' },
            { id: 'group-therapy', label: 'Group Therapy (sessions with other teens)' },
            { id: 'psychiatric-care', label: 'Psychiatric Evaluation or Medication Management' },
            { id: 'counseling', label: 'Counseling or Skills Training' },
            { id: 'all', label: 'All services as recommended by therapist' },
          ],
        },
        {
          type: 'paragraph',
          text: "Your involvement based on your child's age:",
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Children under 13: You must be present and accessible during telehealth sessions. Your therapist may briefly step away to speak with you privately.',
            'Teenagers ages 13-17: Your therapist may request some time alone with your child for private, age-appropriate conversation. This is normal therapeutic practice. The therapist will discuss this with you beforehand and will contact you if safety concerns arise.',
            'Family therapy or parent sessions: You will be asked to participate directly in the video or phone session.',
          ],
        },
      ],
    },
    {
      id: 'confidentiality',
      navLabel: "Child's Privacy Rights",
      heading: "Confidentiality and Your Child's Privacy Rights",
      blocks: [
        {
          type: 'paragraph',
          text: 'What your therapist keeps private:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Session content: What your child shares with their therapist is generally kept confidential.',
            'State law variations: Some states allow teenagers (age 12, 14, or 16+, depending on state) to have certain health information kept confidential from parents, including mental health counseling and substance use treatment.',
            'Therapy notes: Detailed session notes may be kept separate from your access under HIPAA protections.',
            "Your therapist will explain your state's specific confidentiality rules at the start of treatment.",
          ],
        },
        {
          type: 'paragraph',
          text: 'When your therapist WILL tell you:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Imminent safety risk: If your child is at immediate risk of hurting themselves or someone else',
            'Abuse or neglect: If your child discloses abuse, neglect, or exploitation',
            'Progress updates: General updates on treatment progress (unless state law restricts this)',
            'Medication concerns: Any adverse reactions or medication management issues',
            'Emergency situations: If immediate medical or psychiatric intervention is needed',
          ],
        },
        {
          type: 'paragraph',
          text: 'Important: You will not receive a detailed transcript of every session. Your therapist will share clinically relevant information needed to keep your child safe.',
        },
      ],
    },
    {
      id: 'benefits',
      navLabel: 'Benefits',
      heading: 'Benefits of Telehealth Therapy for Teens',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Convenience: No travel time; sessions from home',
            'Flexibility: Easier to fit around school and activities',
            'Comfort: Some teens feel more comfortable talking from a familiar environment',
            'Continuity: Can maintain care even during busy schedules or weather disruptions',
            'Same quality: Telehealth therapy is as effective as in-person therapy for most issues',
            'Access: May have more providers available than in-person options in your area',
          ],
        },
      ],
    },
    {
      id: 'risks',
      navLabel: 'Risks & Limits',
      heading: 'Possible Risks and Limitations of Telehealth',
      blocks: [
        {
          type: 'bullet-list',
          items: [
            'Internet connection problems may interrupt or end a session',
            'Your therapist cannot perform a physical examination if needed',
            'Video or audio quality may sometimes be poor or unstable',
            'If others are in your home, they may overhear parts of the session (we recommend headphones)',
            'Your therapist may determine that in-person care is more appropriate',
            'There is a small risk someone could try to access the session without permission, though we use secure encryption',
            'Telehealth is NOT appropriate for emergencies (call 911 or go to emergency room)',
          ],
        },
      ],
    },
    {
      id: 'privacy-setup',
      navLabel: 'Setting Up Safe Sessions',
      heading: 'How Your Child Can Have a Private Session',
      blocks: [
        {
          type: 'paragraph',
          text: "To protect your child's privacy during telehealth:",
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Find a private, quiet place in your home where others cannot hear',
            'Use headphones or keep volume low so siblings or family members cannot listen in',
            'Tell household members not to interrupt during the session',
            'Ensure the camera view shows only your child (not family members or personal items)',
            'Your child should wear appropriate clothing as they would for an in-person appointment',
          ],
        },
        {
          type: 'paragraph',
          text: 'What is NOT allowed:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Recording the session (video or audio) without therapist permission',
            'Taking screenshots or photos of the screen',
            'Sharing session link or access code with anyone else',
          ],
        },
      ],
    },
    {
      id: 'emergency',
      navLabel: 'Mental Health Emergency',
      heading: 'What to Do If Your Child Is in Crisis',
      blocks: [
        {
          type: 'paragraph',
          text: 'Telehealth is NOT for emergencies. If your child is having thoughts of suicide, self-harm, or any other crisis:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'Call 911 or go to the nearest emergency room immediately',
            'Call 988 - National Suicide Prevention Lifeline (available 24/7, call or text)',
            'Text "HELLO" to 741741 - Crisis Text Line (available 24/7)',
            'Call your local crisis services or mental health emergency line',
            "Contact your child's therapist during business hours for non-emergency concerns",
          ],
        },
      ],
    },
    {
      id: 'provider-credentials',
      navLabel: 'Provider Info',
      heading: "Your Child's Therapist's Credentials",
      blocks: [
        {
          type: 'paragraph',
          text: "Your child's therapist is a licensed mental health professional. You have the right to:",
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            "Know your therapist's name, credentials, and license number",
            'Verify their licensure with your state licensing board',
            'Know if they are a supervised intern or associate (they will tell you)',
            'Understand their training, experience, and therapeutic approach',
            'Ask questions about their qualifications at any time',
          ],
        },
      ],
    },
    {
      id: 'assent',
      navLabel: "Child's Agreement",
      heading: "Your Child's Agreement to Participate",
      blocks: [
        {
          type: 'paragraph',
          text: 'Your child should understand and agree to telehealth therapy. This is called "assent" — even though you as the parent are giving legal consent, your child should also agree to participate.',
          bold: true,
        },
        {
          type: 'paragraph',
          text: 'Talk with your child about:',
        },
        {
          type: 'bullet-list',
          items: [
            'What therapy is and why they are going',
            'That therapy is a private conversation between them and their therapist',
            'That some information will be kept private (depending on age and state law)',
            "That they can ask questions or say if they don't want to continue",
            'That the therapist will help them with their feelings and challenges',
          ],
        },
      ],
    },
    {
      id: 'understand',
      navLabel: 'Before You Sign',
      heading: 'Before You Sign This Consent',
      blocks: [
        {
          type: 'paragraph',
          text: 'By signing this form, you are confirming:',
          bold: true,
        },
        {
          type: 'bullet-list',
          items: [
            'You are the legal parent or guardian of the minor named above',
            'You have the legal authority to provide consent for their healthcare',
            'You understand what telehealth therapy is and how it works',
            'You understand the benefits and risks of telehealth for your child',
            'You have discussed telehealth with your child and they have agreed to participate',
            "You understand your child's privacy rights and state confidentiality laws",
            'You understand when the therapist must break confidentiality (safety, abuse, court orders)',
            'You understand that you can withdraw this consent at any time by notifying the provider in writing',
            'You authorize telehealth services through HIPAA LINK for your child',
          ],
        },
        {
          type: 'paragraph',
          text: 'Do NOT sign this form unless you and your child both agree to telehealth therapy.',
        },
      ],
    },
    {
      id: 'sign',
      navLabel: 'Signature',
      heading: 'Your Signature',
      blocks: [
        {
          type: 'field-group',
          fields: [
            {
              type: 'text',
              id: 'guardianPrintedName',
              label: 'Your Name (print)',
              placeholder: 'Print your full name',
              required: true,
              fullWidth: true,
            },
            {
              type: 'select',
              id: 'guardianRole',
              label: 'Your Legal Role',
              options: [
                { value: 'parent', label: 'Parent' },
                { value: 'legal-guardian', label: 'Legal Guardian' },
                { value: 'custodian', label: 'Custodial Parent' },
              ],
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'By signing below, I confirm that I am the legal parent or guardian of the minor child named above, and I authorize telehealth behavioral health services to be provided to my child through HIPAA LINK.',
        },
        { type: 'signature' },
      ],
    },
  ],
  requiredFields: [
    'minorFirstName',
    'minorLastName',
    'minorDob',
    'guardianFirstName',
    'guardianLastName',
    'guardianPhone',
    'signature',
    'guardianPrintedName',
  ],
};
